import { useCallback } from 'react';
import type { ContactPlatform } from '@/components/contact/contact-drawer-url-state';
import type { ContactView } from '@/components/contact/contact-views';
import { useOpenContactDrawer } from '@/hooks/use-open-contact-drawer';

export interface ExistingCreatorSurfaceState {
  /** The backend-issued creator DB id — metadata the caller has on hand. */
  id: string;
  /** The creator's social username — resolves the unified contact drawer. */
  username: string;
}

export interface OpenExistingCreatorSurfaceOptions {
  /** Selects the IG/TikTok profile view; omitted resolves to `'ig'` via the URL-state default. */
  platform?: ContactPlatform;
  /** Detail view to land on (e.g. `'outreach'` to open straight into the email composer). */
  view?: ContactView;
}

/**
 * Opens the unified contact drawer for a creator already tracked in the brand's
 * creator list.
 *
 * Resolves the creator by `username`; `platform` selects the IG/TikTok profile
 * view (omitted → `'ig'` via the URL-state default) and `view` lands the drawer
 * on a detail view (e.g. `'outreach'`).
 *
 * Returns a stable callback so callers can pass it straight to a click handler.
 */
export const useOpenExistingCreatorSurface = () => {
  const { open } = useOpenContactDrawer();

  return useCallback(
    (state: ExistingCreatorSurfaceState, opts?: OpenExistingCreatorSurfaceOptions) => {
      open({ username: state.username }, { platform: opts?.platform, view: opts?.view });
    },
    [open]
  );
};
